import React, { useEffect, useState } from 'react';
import { BrowserRouter, Route, Switch } from 'react-router-dom';
import { CountryCode } from '../urls-config';
import { useTrimContext } from './hooks/useTrimContext';
import NavFooter from './PnfFooter';
import NavHeader from './NavHeader';
import PNFDisputeLandingPage from './dispute/PNFDisputeLandingPage';
import PNFDisputeSignInPage from './dispute/PNFDisputeSignInPage';
import PnfLandingPage from './PnfLandingPage';
import PnfSignInPage from './PnfSignInPage';
import { MbmProvider, useBundle } from '@amzn/react-arb-tools';
import { PublicNoticeFormStrings } from 'src/common/constants';
import { KatSpinner } from '@amzn/katal-react';
import { getMbmOptions } from 'src/common/helpers';

function AppWithTranslationLoaded(props: {currentLanguage: string, setCurrentLanguage: (lang: string) => void}) {
  const { context } = useTrimContext();
  const {currentLanguage, setCurrentLanguage} = props;
  
  return (
    <>
      {context && (
        <NavHeader
          authenticationState={context.authenticationState}
          currentLocale={currentLanguage}
          isDisputeEnabled={context.isDisputeEnabled}
          supportedLanguages={context.supportedLanguages}
          setCurrentLanguage={setCurrentLanguage}
        />
      )}
      <BrowserRouter>
        <Switch>
          <Route
            exact
            path="/report/dispute"
            key={`dispute-landing-page-${currentLanguage}`}
            render={(props) => (
              <PNFDisputeLandingPage
                authenticationState={context?.authenticationState}
                routerProps={props}
                userFirstName={context?.firstName || ''}
              />
            )}
          />
          <Route
            exact
            path="/report/dispute/signin"
            key={`pnf-dispute-signin-page-${currentLanguage}`}
            render={() => (
              <>
                <div className="page-container">
                  <PNFDisputeSignInPage
                    authenticationState={context?.authenticationState}
                  />
                </div>
              </>
            )}
          />
          <Route
            exact
            path="/report/infringement/retract"
            key={`pnf-landing-page-${currentLanguage}`}
            render={(props) => (
              <PnfLandingPage
                authenticationState={context?.authenticationState}
                currentLanguage={currentLanguage}
                domainCountryCode={context?.domainCountryCode || CountryCode.US}
                routerProps={props}
                userFirstName={context?.firstName || ''}
              />
            )}
          />
          <Route
            exact
            path="/report/infringement/retract/signin"
            key={`pnf-signin-page-${currentLanguage}`}
            render={() => (
              <>
                <div className="page-container">
                  <PnfSignInPage
                    authenticationState={context?.authenticationState}
                    domainCountryCode={context?.domainCountryCode || CountryCode.US}
                  />
                </div>
              </>
            )}
          />
          <Route
            exact
            path="/report/infringement"
            render={(props) => (
              <>
                <div className="page-container">
                  <PnfLandingPage
                    authenticationState={context?.authenticationState}
                    currentLanguage={currentLanguage}
                    domainCountryCode={context?.domainCountryCode || CountryCode.US}
                    routerProps={props}
                    userFirstName={context?.firstName || ''}
                  />
                </div>
              </>
            )}
          />
          <Route
            exact
            path="/report/infringement/signin"
            key={`pnf-infringement-signin-page-${currentLanguage}`}
            render={() => (
              <>
                <div className="page-container">
                  <PnfSignInPage
                    authenticationState={context?.authenticationState}
                    domainCountryCode={context?.domainCountryCode || CountryCode.US}
                  />
                </div>
              </>
            )}
          />
        </Switch>
      </BrowserRouter>
      {context && <NavFooter footerLinks={context.footerLinks} />}
    </>
  );
}

export const App = (props: any) => {

  // If there's a language preference saved in local storage, it replaces the html tag lang value to the saved one
  const changePageLanguage = (savedLanguage: string) => {
    document?.querySelector('html')!.setAttribute('lang', savedLanguage);
  };

  // Get language from localStorage if available, otherwise use the HTML lang attribute
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    const savedLanguage = localStorage.getItem('preferredLanguage');
    return savedLanguage || document?.querySelector('html')!.getAttribute('lang') || 'en-US';
  });

  useEffect(() => {
    if (localStorage.getItem('preferredLanguage')) {
      changePageLanguage(localStorage.getItem('preferredLanguage') || 'en-US');
    }
  }, []);

  // Update localStorage when language changes
  const handleLanguageChange = (lang: string) => {
    localStorage.setItem('preferredLanguage', lang);
    setCurrentLanguage(lang);
  };

  return (
    <MbmProvider
      key={`mbm-provider-${currentLanguage}`}
      {...getMbmOptions(currentLanguage)}
    >
      <AppWithTranslation
        currentLanguage={currentLanguage}
        setCurrentLanguage={handleLanguageChange}
        {...props}
      />
    </MbmProvider>
  );
};

const AppWithTranslation = (props: any) => {
  const [pnfStrings, isBundleLoading] = useBundle(PublicNoticeFormStrings);

  if (isBundleLoading) {
    return <KatSpinner size="large" />;
  }

  return <AppWithTranslationLoaded {...props} />;
};